import React, { useContext, useState, useRef, useEffect } from 'react';
import classNames from 'classnames';
import { createUseStyles } from 'react-jss';
import { ThemeContext } from 'styled-components';
import PropTypes from 'prop-types';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSortAlt } from '@fortawesome/pro-regular-svg-icons';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';

import { Typography } from '@comparethemarketau/manor-typography';

import { MenuItem } from './menu_item';
import { withSortState, WithSortStateTypes, SortDirType } from './with_sort_state';
import { withAvailableQuotes } from '../../products/common/with_available_quotes';

const useStyles = createUseStyles((theme) => ({
    container: {
        position: 'relative'
    },
    sortIcon: {
        marginRight: theme.spacing[8],
        alignSelf: 'flex-end'
    },
    button: {
        cursor: 'pointer',
        border: 'none',
        backgroundColor: 'transparent',
        display: 'flex',
        alignItems: 'center',
        color: theme.colors.primary500,
        fontWeight: theme.fontWeight.bold,
        height: '2rem',
        '&:focus': {
            border: 'none',
            outline: theme.dropdown.list.item.borderFocus,
            borderRadius: theme.borderRadius.default
        }
    },
    buttonDisabled: {
        color: theme.colors.grey500,
        cursor: 'no-drop'
    },
    dropdownMenu: {
        position: 'absolute',
        width: 'max-content',
        right: 0,
        top: `calc(100% + ${theme.spacing[4]})`,
        backgroundColor: theme.colors.white,
        boxShadow: theme.boxShadow.sm,
        borderRadius: theme.borderRadius.default,
        transform: `translateY(-${theme.spacing[8]})`,
        transition: [
            ['opacity', '100ms', 'ease-in-out'],
            ['transform', '100ms', 'ease-in-out']
        ],
        opacity: 0,
        pointerEvents: 'none'
    },
    active: {
        opacity: 1,
        transform: 'translateY(0)',
        pointerEvents: 'auto',
        zIndex: '1'
    }
}));

const _ProductsSortSelect = (props) => {
    const theme = useContext(ThemeContext);
    const classes = useStyles({ theme });

    const { sortResultsByPrice, paymentFrequencySelected, sortDir, availableQuotesSize } = props;

    const [active, setActive] = useState(false);
    const buttonRef = useRef();
    const optionsLength = 2;
    const [focusedIndex, setFocusedIndex] = useState(0);

    const handleButtonClick = () => {
        setActive(!active);
    };

    const handleClickAway = () => {
        setActive(false);
    };

    const handleChange = (value) => {
        setActive(false);
        sortResultsByPrice(paymentFrequencySelected, value);
    };

    useEffect(
        () => {
            if (!active) {
                setFocusedIndex(0);
            }
        }, [active]
    );

    const handleKeyDown = (event) => {
        switch (event.key) {
            case 'ArrowDown':
                event.preventDefault();
                if (focusedIndex <= 0 || focusedIndex === optionsLength) {
                    setFocusedIndex(1);
                } else {
                    setFocusedIndex(focusedIndex + 1);
                }
                break;
            case 'ArrowUp':
                event.preventDefault();
                if (focusedIndex > 1) {
                    setFocusedIndex(focusedIndex - 1);
                } else if (focusedIndex === 1) {
                    setFocusedIndex(optionsLength);
                } else {
                    setFocusedIndex(1);
                }
                break;
            case 'Esc': // IE/Edge specific value
            case 'Escape':
                setActive(false);
                buttonRef.current.focus();
                break;
            default:
        }
    };

    return (
        <ClickAwayListener onClickAway={handleClickAway}>
            <div className={classNames('test-policySorts-select', classes.container)} onKeyDown={handleKeyDown} role="none" data-testid="policySorts-select">
                <button
                    ref={buttonRef}
                    className={classNames(classes.button, availableQuotesSize === 0 && classes.buttonDisabled)}
                    type="button"
                    onClick={handleButtonClick}
                    disabled={availableQuotesSize === 0}
                >
                    <FontAwesomeIcon icon={faSortAlt} className={classes.sortIcon} />
                    <Typography variant="subtitle2">{`Sort: ${sortDir === SortDirType.ASC ? 'Price (Low-High)' : 'Price (High-Low)'}`}</Typography>
                </button>
                <div className={classNames(classes.dropdownMenu, active && classes.active)}>
                    <div role="listbox" tabIndex="-1">
                        <MenuItem selected={sortDir === SortDirType.ASC} value={SortDirType.ASC} onChange={handleChange} index={1} focusedIndex={focusedIndex}>
                            {'Price (Low-High)'}
                        </MenuItem>
                        <MenuItem selected={sortDir === SortDirType.DESC} value={SortDirType.DESC} onChange={handleChange} index={2} focusedIndex={focusedIndex}>
                            {'Price (High-Low)'}
                        </MenuItem>
                    </div>
                </div>
            </div>
        </ClickAwayListener>
    );
};

_ProductsSortSelect.propTypes = {
    ...WithSortStateTypes,
    availableQuotesSize: PropTypes.number
};

const ProductsSortSelect = withAvailableQuotes(withSortState(_ProductsSortSelect));
export { ProductsSortSelect, _ProductsSortSelect };
